<template>
  <v-container class="py-6">
    <v-card class="pa-6 rounded-lg elevation-2 border-top-accent">
      <v-card-title class="d-flex align-center pb-0">
        <v-icon left color="primary" size="32">mdi-email-outline</v-icon>
        <span class="text-h5 font-weight-bold ml-2">Configuración de Correos</span>
        <HelpHint class="ml-2" text="Define SMTP para envío de comprobantes. Si falla la conexión o autenticación, revisa host/puerto/usuario/TLS." />
      </v-card-title>
      <v-card-subtitle class="text-subtitle-1 mb-4">
        Conecta tu servidor de correo electrónico (SMTP) para enviar documentos directamente a tus clientes.
      </v-card-subtitle>

      <v-divider class="mb-6"></v-divider>

      <v-form ref="form" v-model="valid">
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.host"
              label="Servidor SMTP"
              placeholder="smtp.example.com"
              outlined
              dense
              class="mb-2"
              required
            ></v-text-field>

            <v-text-field
              v-model="form.port"
              label="Puerto SMTP"
              placeholder="587 o 465"
              type="number"
              outlined
              dense
              class="mb-2"
              required
            ></v-text-field>

            <v-switch
              v-model="form.secure"
              label="Usar SSL/TLS (Puerto 465)"
              color="primary"
              class="mb-2 mt-0"
            ></v-switch>

            <v-text-field
              v-model="form.user"
              label="Usuario / Email SMTP"
              outlined
              dense
              class="mb-2"
            ></v-text-field>

            <v-text-field
              v-model="form.password"
              label="Contraseña"
              type="password"
              outlined
              dense
              class="mb-2"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.fromName"
              label="Nombre del Remitente"
              placeholder="HerraMetal Ventas"
              outlined
              dense
              class="mb-2"
            ></v-text-field>

            <v-text-field
              v-model="form.fromEmail"
              label="Email del Remitente"
              placeholder="noreply@tusitio.com"
              outlined
              dense
              class="mb-2"
            ></v-text-field>

            <!-- Test section inside Email Config card -->
            <v-card class="pa-4 rounded-lg grey lighten-4 elevation-1 mt-4">
              <span class="text-subtitle-2 font-weight-bold d-block mb-2">Envío de Prueba</span>
              <p class="caption mb-3">Ingresa un correo electrónico para verificar la configuración de tu servidor SMTP.</p>
              
              <v-text-field
                v-model="testToEmail"
                label="Email de Destino"
                placeholder="test@example.com"
                outlined
                dense
                background-color="white"
                class="mb-2"
              ></v-text-field>

              <v-btn
                color="secondary"
                :loading="testing"
                @click="testEmail"
                dense
                elevation="1"
                class="px-4 mt-2"
              >
                <v-icon left>mdi-send-outline</v-icon>
                Enviar correo de prueba
              </v-btn>

              <div v-if="testMsg" :class="['mt-2 text-caption font-weight-bold animate-fade', testIsError ? 'error-text' : 'success-text']">
                {{ testMsg }}
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-divider class="mt-4 mb-4"></v-divider>

        <v-btn
          color="primary"
          :loading="saving"
          @click="save"
          large
          elevation="2"
          class="px-6 font-weight-bold"
        >
          <v-icon left>mdi-content-save-outline</v-icon>
          Guardar Configuración
        </v-btn>

        <span v-if="successMsg" class="ml-4 success-text font-weight-bold animate-fade">{{ successMsg }}</span>
      </v-form>
    </v-card>
  </v-container>
</template>

<script>
import api from "../services/api";
import HelpHint from "@/components/HelpHint.vue";
import { parseApiError } from "@/utils/errors";

export default {
  name: "EmailConfigView",
  components: { HelpHint },
  data() {
    return {
      valid: true,
      saving: false,
      testing: false,
      successMsg: "",
      testMsg: "",
      testIsError: false,
      testToEmail: "",
      form: {
        host: "",
        port: 587,
        secure: false,
        user: "",
        password: "",
        fromName: "",
        fromEmail: ""
      }
    };
  },
  async created() {
    await this.fetchConfig();
  },
  methods: {
    async fetchConfig() {
      try {
        const res = await api.get("/email-config");
        if (res.data && res.data.data) {
          const d = res.data.data;
          this.form = {
            host: d.host || "",
            port: d.port || 587,
            secure: Boolean(d.secure),
            user: d.user || "",
            password: d.password || "",
            fromName: d.fromName || "",
            fromEmail: d.fromEmail || ""
          };
        }
      } catch (err) {
        console.error("Error loading email config:", err);
      }
    },
    async save() {
      this.saving = true;
      this.successMsg = "";
      try {
        const res = await api.post("/email-config", this.form);
        if (res.data && res.data.success) {
          this.successMsg = "¡Guardado exitosamente!";
          setTimeout(() => {
            this.successMsg = "";
          }, 3500);
        }
      } catch (err) {
        this.successMsg = parseApiError(err, "Error guardando configuración SMTP.");
      } finally {
        this.saving = false;
      }
    },
    async testEmail() {
      if (!this.testToEmail) {
        this.testMsg = "Por favor ingresa un correo electrónico de destino.";
        this.testIsError = true;
        return;
      }
      this.testing = true;
      this.testMsg = "";
      this.testIsError = false;
      try {
        const payload = {
          ...this.form,
          toEmail: this.testToEmail
        };
        const res = await api.post("/email-config/test", payload);
        if (res.data && res.data.success) {
          this.testMsg = "¡Correo de prueba enviado exitosamente!";
          this.testIsError = false;
        } else {
          this.testMsg = res.data.error || "No se pudo enviar el correo de prueba.";
          this.testIsError = true;
        }
      } catch (err) {
        this.testMsg = parseApiError(err, "Error en la conexión con el servidor.");
        this.testIsError = true;
      } finally {
        this.testing = false;
      }
    }
  }
};
</script>

<style scoped>
.border-top-accent {
  border-top: 4px solid var(--v-primary-base, #1976D2);
}
.success-text {
  color: #4caf50;
}
.error-text {
  color: #f44336;
}
.animate-fade {
  animation: fadeIn 0.5s ease;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
